import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import "./Privacy.css";
import LandingNavbar from "../../components/LandingNavbar/LandingNavbar";
import TopBanner from "../../components/TopBanner/TopBanner";
import Footer from "../../components/Footer/Footer";

const Privacy = () => {
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    const sections = document.querySelectorAll(".privacy-section");

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSection = entries.find(
          (entry) => entry.isIntersecting
        );

        if (visibleSection) {
          setActiveSection(visibleSection.target.id);
        }
      },
      {
        rootMargin: "-15% 0px -65% 0px",
        threshold: 0,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
  <main className="privacy-page">

    <Helmet>
      <title>Privacy Policy | Xyncely</title>

      <meta
        name="description"
        content="Learn how Xyncely collects, uses, stores and protects information when you use its website, products and services."
      />

      <meta
        name="robots"
        content="noindex,follow"
      />

      <link
        rel="canonical"
        href="https://xyncely.com/privacy"
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Xyncely" />
      <meta property="og:locale" content="en_IN" />

      <meta
        property="og:title"
        content="Privacy Policy | Xyncely"
      />

      <meta
        property="og:description"
        content="Learn how Xyncely collects, uses, stores and protects information when you use its website, products and services."
      />

      <meta
        property="og:url"
        content="https://xyncely.com/privacy"
      />

      <meta
        property="og:image"
        content="https://xyncely.com/assets/xyncely-og-1200x630.png"
      />

      <meta
        property="og:image:type"
        content="image/png"
      />

      <meta
        property="og:image:width"
        content="1200"
      />

      <meta
        property="og:image:height"
        content="630"
      />

      <meta
        property="og:image:alt"
        content="Xyncely - The Command Center for Influencer Marketing"
      />

      {/* Twitter / X */}
      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content="Privacy Policy | Xyncely"
      />

      <meta
        name="twitter:description"
        content="Learn how Xyncely collects, uses, stores and protects information when you use its website, products and services."
      />

      <meta
        name="twitter:image"
        content="https://xyncely.com/assets/xyncely-og-1200x630.png"
      />

      <meta
        name="twitter:image:alt"
        content="Xyncely - The Command Center for Influencer Marketing"
      />
    </Helmet>

    <TopBanner/>
      <LandingNavbar/>

      {/* =========================
          Back To Home
      ========================= */}

      <div className="privacy-page__back">
        <a href="/">
          ← <span>Back to Home</span>
        </a>
      </div>


      {/* =========================
          Hero
      ========================= */}

      <section className="privacy-hero">
        <div className="privacy-hero__content">

          <h1>
            Your privacy,
            <br />
            our commitment.
          </h1>

          <p>
            We believe transparency is foundational. This policy explains
            exactly how Xyncely collects, uses, and protects your information
            — in plain language.
          </p>

        </div>
      </section>


      {/* =========================
          Policy Header
      ========================= */}

      <section className="privacy-document">

        <div className="privacy-document__meta">
          <span>
            EFFECTIVE DATE&nbsp;&nbsp; January 15, 2025
          </span>

          <span>
            LAST UPDATED&nbsp;&nbsp; July 28, 2025
          </span>
        </div>


        <div className="privacy-layout">

          {/* =========================
              Sidebar
          ========================= */}

          <aside className="privacy-sidebar">

            <span className="privacy-sidebar__title">
              ON THIS PAGE
            </span>

            <a
  href="#introduction"
  className={activeSection === "introduction" ? "active" : ""}
>
  Introduction
</a>

<a
  href="#information-we-collect"
  className={
    activeSection === "information-we-collect" ? "active" : ""
  }
>
  Information We Collect
</a>

<a
  href="#how-we-use"
  className={activeSection === "how-we-use" ? "active" : ""}
>
  How We Use Your Information
</a>

<a
  href="#information-sharing"
  className={
    activeSection === "information-sharing" ? "active" : ""
  }
>
  Information Sharing
</a>

<a
  href="#data-retention"
  className={
    activeSection === "data-retention" ? "active" : ""
  }
>
  Data Retention
</a>

<a
  href="#your-rights"
  className={activeSection === "your-rights" ? "active" : ""}
>
  Your Rights
</a>

<a
  href="#cookies"
  className={activeSection === "cookies" ? "active" : ""}
>
  Cookies & Tracking
</a>

<a
  href="#security"
  className={activeSection === "security" ? "active" : ""}
>
  Security
</a>

<a
  href="#childrens-privacy"
  className={
    activeSection === "childrens-privacy" ? "active" : ""
  }
>
  Children's Privacy
</a>

<a
  href="#changes"
  className={activeSection === "changes" ? "active" : ""}
>
  Changes to This Policy
</a>

<a
  href="#contact"
  className={activeSection === "contact" ? "active" : ""}
>
  Contact Us
</a>
            <div className="privacy-sidebar__question">

              <strong>Questions?</strong>

              <p>
                Our team is here to help you understand how we handle your
                data.
              </p>

              <a href="mailto:support@xyncely.com">
                support@xyncely.com ↗
              </a>

            </div>

          </aside>


          {/* =========================
              Main Content
          ========================= */}

          <article className="privacy-content">

            {/* Introduction */}

            <section id="introduction" className="privacy-section">

              <h2>Introduction</h2>

              <p>
                Welcome to Xyncely ("we," "our," or "us"). We operate the
                Xyncely influencer marketing command center, including our
                website at xyncely.com and all associated services
                (collectively, the "Service").
              </p>

              <p>
                This Privacy Policy describes how we collect, use, disclose,
                and safeguard your information when you use our Service. By
                accessing or using Xyncely, you agree to the terms outlined in
                this policy. If you disagree with any part, please discontinue
                use of our Service.
              </p>

              <p>
                We are committed to handling your personal data responsibly
                and in accordance with applicable data protection laws,
                including the General Data Protection Regulation (GDPR), the
                California Consumer Privacy Act (CCPA), and other relevant
                legislation.
              </p>

            </section>


            {/* Information We Collect */}

            <section
              id="information-we-collect"
              className="privacy-section"
            >

              <h2>Information We Collect</h2>

              <p>
                We collect several types of information to provide and improve
                our Service to you.
              </p>


              <div className="privacy-info-card">

                <div>
                  <h3>Account Information</h3>

                  <p>
                    When you register, we collect your name, email address,
                    company name, job title, and password. For paid accounts,
                    billing details are handled by our PCI-compliant payment
                    processor.
                  </p>
                </div>


                <div>
                  <h3>Usage Data</h3>

                  <p>
                    We automatically collect information on how you interact
                    with the Service — including pages visited, features used,
                    session duration, click paths, and search queries within
                    the platform.
                  </p>
                </div>


                <div>
                  <h3>Campaign & Creator Data</h3>

                  <p>
                    Information you or create within the platform — campaign
                    briefs, influencer profiles, performance metrics,
                    contracts, and correspondence — is stored and processed
                    on your behalf.
                  </p>
                </div>


                <div>
                  <h3>Device & Technical Data</h3>

                  <p>
                    IP address, browser type and version, operating system,
                    device identifiers, time zone, language preference, and
                    referring URLs.
                  </p>
                </div>

              </div>


              <p>
                We do not sell, rent, or trade your personal information to
                third parties for their marketing purposes. Full stop.
              </p>

            </section>


            {/* How We Use */}

            <section id="how-we-use" className="privacy-section">

              <h2>How We Use Your Information</h2>

              <p>
                We use the information we collect for the following purposes:
              </p>

              <ul className="privacy-list">

                <li>
                  <strong>Providing the Service: </strong>
                   Delivering, operating, and maintaining your Xyncely
                  workspace and all associated features.
                </li>

                <li>
                  <strong>Personalization: </strong>
                  Tailoring dashboard views, recommendations, and insights
                  based on your campaigns and preferences.
                </li>

                <li>
                  <strong>Analytics & Improvement: </strong>
                  Understanding usage patterns to improve existing features
                  and develop new ones that better serve your workflow.
                </li>

                <li>
                  <strong>Communications: </strong>
                  Sending transactional emails, security alerts, product
                  updates, and — only with your explicit consent — marketing
                  communications.
                </li>

                <li>
                  <strong>Support: </strong>
                  Responding to your inquiries, troubleshooting issues, and
                  providing technical assistance.
                </li>

                <li>
                  <strong>Legal & Compliance: </strong>
                  Meeting our legal obligations, resolving disputes, and
                  enforcing our agreements.
                </li>

              </ul>

            </section>


            {/* Information Sharing */}

            <section
              id="information-sharing"
              className="privacy-section"
            >

              <h2>Information Sharing</h2>

              <p>
                We do not sell or rent your personal information. We share
                data only in the following limited circumstances:
              </p>


              <div className="privacy-info-card privacy-info-card--compact">

                <div>
                  <h3>Service Providers</h3>

                  <p>
                    Trusted third-party vendors who help us operate Xyncely —
                    cloud hosting, analytics, email delivery, and customer
                    support tools. Each is bound by data processing agreements.
                  </p>
                </div>


                <div>
                  <h3>Business Transfers</h3>

                  <p>
                    If Xyncely is acquired, merged, or its assets are
                    transferred, your data may be part of that transaction.
                    We will notify you and provide opt-out options where
                    legally required.
                  </p>
                </div>


                <div>
                  <h3>Legal Requirements</h3>

                  <p>
                    When required by law, court order, or government
                    authority, or when we believe disclosure is necessary to
                    protect rights, safety, or property.
                  </p>
                </div>


                <div>
                  <h3>With Your Consent</h3>

                  <p>
                    In any other circumstance, only with your explicit
                    consent.
                  </p>
                </div>

              </div>

            </section>


            {/* Data Retention */}

            <section id="data-retention" className="privacy-section">

              <h2>Data Retention</h2>

              <p>
                We retain your personal data for as long as your account is
                active or as needed to provide the Service. When you close
                your account, we initiate a deletion process within 30 days,
                with complete removal from our systems within 90 days, except
                where retention is required by law.
              </p>

              <p>
                Aggregated, anonymized data derived from your usage may be
                retained indefinitely for product analytics and benchmarking
                purposes — this data cannot be linked back to you.
              </p>


              <div className="privacy-notice">
                <span>▣</span>

                <p>
                  <strong>Data export available: </strong> You can export all
                  your campaign data, influencer records, and account
                  information at any time from Settings → Data Export.
                </p>
              </div>

            </section>


            {/* Your Rights */}

            <section id="your-rights" className="privacy-section">

              <h2>Your Rights</h2>

              <p>
                Depending on your location, you may have the following rights
                with respect to your personal data:
              </p>


              <div className="privacy-rights">

                <div>
                  <strong>Access</strong>
                  <span>Request a copy of your personal data</span>
                </div>

                <div>
                  <strong>Correction</strong>
                  <span>Update inaccurate or incomplete data</span>
                </div>

                <div>
                  <strong>Deletion</strong>
                  <span>Request erasure of your personal data</span>
                </div>

                <div>
                  <strong>Portability</strong>
                  <span>Receive your data in a machine-readable format</span>
                </div>

                <div>
                  <strong>Restriction</strong>
                  <span>Limit how we process your data</span>
                </div>

                <div>
                  <strong>Objection</strong>
                  <span>Object to processing based on legitimate interests</span>
                </div>

                <div>
                  <strong>Opt-out</strong>
                  <span>Withdraw consent for marketing communications</span>
                </div>

                <div>
                  <strong>Non-discrimination</strong>
                  <span>Exercise rights without discriminatory treatment</span>
                </div>

              </div>


              <p>
                To exercise any of these rights, email{" "}
                <a href="mailto:privacy@xyncely.com">
                  privacy@xyncely.com
                </a>
                . We will respond within 30 days. California residents may
                also submit requests via our in-app Privacy Center.
              </p>

            </section>


            {/* Cookies */}

            <section id="cookies" className="privacy-section">

              <h2>Cookies & Tracking</h2>

              <p>
  We use cookies and similar tracking technologies to enhance
  your experience, understand usage patterns, measure website
  performance, and improve our services. This includes Google
  Analytics, which helps us understand how visitors use our
  website, including pages viewed, interactions, device and
  browser information, approximate location derived from
  technical information, and traffic sources.
</p>

<p>
  Analytics data is used for measurement and service improvement
  purposes and is not used to sell your personal information.
  You can manage or disable non-essential cookies through your
  browser settings and, where available, our Cookie Manager.
</p>


              <div className="privacy-table-wrapper">

                <table className="privacy-table">

                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                    </tr>
                  </thead>

                  <tbody>

                    <tr>
                      <td>Essential</td>
                      <td>Authentication, security, session management</td>
                      <td>Session / 30 days</td>
                    </tr>

                    <tr>
                      <td>Analytics</td>
                      <td>Usage patterns, feature adoption, error tracking</td>
                      <td>Up to 2 years</td>
                    </tr>

                    <tr>
                      <td>Preference</td>
                      <td>Dashboard layout, theme, language settings</td>
                      <td>1 year</td>
                    </tr>

                    <tr>
                      <td>Marketing</td>
                      <td>Campaign attribution (consent-gated)</td>
                      <td>90 days</td>
                    </tr>

                  </tbody>

                </table>

              </div>


              <p>
                You can manage cookie preferences through your browser
                settings or our in-app Cookie Manager. Disabling
                non-essential cookies will not affect core platform
                functionality.
              </p>

            </section>


            {/* Security */}

            <section id="security" className="privacy-section">

              <h2>Security</h2>

              <p>
                Protecting your data is a first-class concern at Xyncely. We
                implement industry-standard safeguards across the stack:
              </p>


              <div className="privacy-badges">

                <span>AES-256 encryption at rest</span>
                <span>TLS 1.3 in transit</span>
                <span>SOC 2 Type II</span>
                <span>GDPR compliant</span>
                <span>ISO / EAL 2.0</span>
                <span>MFA enforced</span>
                <span>Penetration tested quarterly</span>
                <span>Role-based access control</span>
                <span>Audit logs</span>
                <span>99.9% uptime SLA</span>

              </div>


              <p>
                Despite our efforts, no method of electronic transmission or
                storage is 100% secure. If you discover a potential security
                vulnerability, please report it responsibly to{" "}
                <a href="mailto:security@xyncely.com">
                  security@xyncely.com
                </a>
                .
              </p>

            </section>


            {/* Children's Privacy */}

            <section
              id="childrens-privacy"
              className="privacy-section"
            >

              <h2>Children's Privacy</h2>

              <p>
                Our Service is designed for business professionals and is not
                directed to individuals under 16 years of age. We do not
                knowingly collect personal information from children under 16.
              </p>

              <p>
                If you are a parent or guardian and believe your child has
                provided us with personal information, please contact us at{" "}
                <a href="mailto:privacy@xyncely.com">
                  privacy@xyncely.com
                </a>
                . Upon verification, we will promptly delete any such
                information.
              </p>

            </section>


            {/* Changes */}

            <section id="changes" className="privacy-section">

              <h2>Changes to This Policy</h2>

              <p>
                We may update this Privacy Policy from time to time. When we
                make material changes, we will notify you by email (to the
                address on your account) and display a prominent notice within
                the platform at least 14 days before the changes take effect.
              </p>

              <p>
                For non-material changes — such as clarifications or
                formatting updates — we will update the "Last updated" date
                at the top of this policy. Continued use of the Service after
                the effective date constitutes acceptance of the revised
                policy.
              </p>


              <div className="privacy-version">

                <div className="privacy-version__header">
                  Version history
                </div>

                <div>
                  <span>July 28, 2025</span>
                  <p>
                    Added cookie table, security badge list, CCPA rights
                    section
                  </p>
                </div>

                <div>
                  <span>March 12, 2025</span>
                  <p>
                    Updated data retention timelines and added portability
                    right
                  </p>
                </div>

                <div>
                  <span>January 15, 2025</span>
                  <p>Initial policy publication</p>
                </div>

              </div>

            </section>


            {/* Contact */}

            <section id="contact" className="privacy-section">

              <h2>Contact Us</h2>

              <p>
                If you have questions, concerns, or requests relating to this
                Privacy Policy or your personal data, please reach out:
              </p>


              <div className="privacy-contact-grid">

                <div>
                  <span>PRIVACY TEAM</span>

                  <a href="mailto:privacy@xyncely.com">
                    privacy@xyncely.com
                  </a>

                  <small>Response within 2 business days</small>
                </div>


                <div>
                  <span>SECURITY ISSUES</span>

                  <a href="mailto:security@xyncely.com">
                    security@xyncely.com
                  </a>

                  <small>For vulnerability disclosures</small>
                </div>

              </div>


              <div className="privacy-contact-company">

                <span>REGISTERED ADDRESS</span>

                <strong>Xyncely</strong>

                <p>
                  Springhouse, B1/622, Janakpuri, New Delhi
                </p>

              </div>

            </section>

          </article>

        </div>

      </section>
      <Footer/>

    </main>
  );
};

export default Privacy;